/**
 * TutorialPopup.js
 * Implements the tutorial popup elements
 */

import {
    Game,
    GameTween,
    GameWindow,
    WindowLayers,
    UiElement,
    UiFrame,
    UiImage,
    UiNineSlice,
    UiText,
    GameDisplayContainer,
    GameSprite,
    CloseEventSource,
    CoreEvents,
    GameAnimatedSprite,
    InputMethod,
    KeyboardKey,
    TINY_INITIAL_SCALE_FACTOR,
    MODAL_OPEN_CLOSE_TIME,
    MODAL_OPEN_CURVE
} from "pixi_core";

import { Common } from "../game/Common";

import {
    GameEvents,
    TUTORIAL_MASKS,
    DEFAULT_TUTORIAL_MESSAGE_WIDTH,
    DEFAULT_TUTORIAL_MESSAGE_HEIGHT,
    TUTORIAL_DARKEN_OPACITY,
    TUTORIAL_INLINE_SPRITE_THRESHOLD,
    TUTORIAL_LABEL_WIDTH_RATIO,
    TUTORIAL_LABEL_STYLE,
    TUTORIAL_LABEL_FONTSIZE,
    TUTORIAL_NB_TURNS_TO_CHANGE,
    TUTORIAL_DARKEN_PADDING_SIZE,
    TUTORIAL_DARKEN_PADDING_SCALE
} from "../Constants";

export class TutorialPopup extends GameWindow {
    /**
     * @public
     */
    constructor() {
        super({
            name: "TutorialPopup",
            disableInputUnder: false,
            showDarkenUnder: false,
            ignoreOutsideZoneTap: true,
            ignoreGearButton: true,
            ignoreAndroidBackButton: true,
            ignoreNavigation: true,
            defaultInstantClose: true,
            defaultInstantOpen: true,
            layer: WindowLayers.eUNDER_GEAR
        });

        Common.tutorial = this;

        /** @private @type {UiElement} */
        this._ui = null;

        /** @private @type {UiImage} */
        this._inputZone = null;

        /** @private @type {UiFrame} */
        this._darkenFrame = null;
        /** @private @type {GameDisplayContainer} */
        this._darken = null;
        /** @private @type {GameSprite} */
        this._darkenMask = null;

        /** @private @type {UiFrame} */
        this._frmMessage1 = null;
        /** @private @type {UiFrame} */
        this._frmMessage2 = null;

        /** @private @type {UiNineSlice} */
        this._background1 = null;
        /** @private @type {UiNineSlice} */
        this._background2 = null;

        /** @private @type {UiText} */
        this._lblMessage1 = null;
        /** @private @type {UiText} */
        this._lblMessage2 = null;

        /** @private @type {Object[]} */
        this._tutorialData = [];

        /** @private @type {UiImage} */
        this._frmHand = null;
        /** @private @type {GameAnimatedSprite} */
        this._animatedHand = null;

        /** @private @type {UiImage} */
        this._heroSelectionHighlight = null;

        /** @private @type {UiFrame} */
        this._heroKeysHighlight1 = null;
        /** @private @type {UiFrame} */
        this._heroKeysHighlight2 = null;

        /** @private @type {String} */
        this._currentTutorial = "";
        /** @private @type {Number} */
        this._currentTutorialTurns = 0;

        /** @private @type {Number} */
        this._currentStep = 0;

        /** @private @type {Map<String, Number[]>} */
        this._stepsOpened = new Map();

        /** @private @type {String[]} */
        this._tutorialToSave = [];

        /** @private @type {Function} */
        this._callback = null;

        /** @private @type {GameTween} */
        this._tweenMessage1 = null;
        /** @private @type {GameTween} */
        this._tweenMessage2 = null;

        /** @private @type {String[]} */
        this._keysToAdvance = [
            KeyboardKey.eKEY_SPACE,
            KeyboardKey.eKEY_ENTER,
            KeyboardKey.eKEY_1,
            KeyboardKey.eKEY_2,
            KeyboardKey.eKEY_3,
            KeyboardKey.eKEY_4,
            KeyboardKey.eKEY_ARROW_LEFT,
            KeyboardKey.eKEY_ARROW_RIGHT,
            KeyboardKey.eKEY_ARROW_DOWN,
            KeyboardKey.eKEY_ARROW_UP
        ];

        Game.on(CoreEvents.eINPUT_KEY_DOWN, this._onKeyDown, this);

        Game.on(GameEvents.eTUTORIAL_HIGHLIGHT_CHANGE_TEAM, this._onHighlightChangeTeam, this);
        Game.on(GameEvents.eTUTORIAL_HIGHLIGHT_CANNON, this._onHighlightCannon, this);
        Game.on(GameEvents.eTUTORIAL_HIGHLIGHT_HERO_ABILITIES, this._onHighlightHeroAbilities, this);
        Game.on(GameEvents.eTUTORIAL_HIGHLIGHT_HERO_ACTIVATION, this._onHighlightHeroActivation, this);
        Game.on(GameEvents.eTUTORIAL_HIGHLIGHT_CLEAR, this._onHighlightClear, this);
        Game.on(GameEvents.eTUTORIAL_RESET_STEPS, this._onResetSteps, this);
    }

    /**
     * The window is opening for the first time
     * @protected
     */
    _onFirstWindowOpening() {
        this._ui = Game.ui.instantiate("Tutorial", this);

        this._inputZone = this._ui.findElementByName("InputZone");
        this._inputZone.sprite.on("pointertap", this._onInputDown.bind(this));

        this._frmMessage1 = this._ui.findElementByName("MessageFrame1");
        this._background1 = this._frmMessage1.findElementByName("Background");

        this._frmMessage2 = this._ui.findElementByName("MessageFrame2");
        this._background2 = this._frmMessage2.findElementByName("Background");

        this._frmHand = this._ui.findElementByName("FrmHand");
        this._frmHand.visible = false;

        this._heroSelectionHighlight = this._ui.findElementByName("HeroSelectionHighlight");
        this._heroSelectionHighlight.visible = false;

        this._heroKeysHighlight1 = this._ui.findElementByName("HeroKeysHighlight1");
        this._heroKeysHighlight1.visible = false;
        this._heroKeysHighlight2 = this._ui.findElementByName("HeroKeysHighlight2");
        this._heroKeysHighlight2.visible = false;

        this._darkenFrame = this._ui.findElementByName("DarkenFrame");

        this._darken = new GameDisplayContainer();
        this._darken.alpha = TUTORIAL_DARKEN_OPACITY;
        this._darkenFrame.uiRoot.addChild(this._darken);

        const paddingL = new GameSprite("MaskTutorialPadding");
        const paddingR = new GameSprite("MaskTutorialPadding");
        paddingL.scale.x = TUTORIAL_DARKEN_PADDING_SCALE;
        paddingL.scale.y = TUTORIAL_DARKEN_PADDING_SCALE;
        paddingL.x = -TUTORIAL_DARKEN_PADDING_SIZE;
        paddingR.scale.x = TUTORIAL_DARKEN_PADDING_SCALE;
        paddingR.scale.y = TUTORIAL_DARKEN_PADDING_SCALE;
        paddingR.x = TUTORIAL_DARKEN_PADDING_SIZE;
        this._darken.addChild(paddingL);
        this._darken.addChild(paddingR);

        const paddingT = new GameSprite("MaskTutorialPadding");
        const paddingB = new GameSprite("MaskTutorialPadding");
        paddingT.scale.x = TUTORIAL_DARKEN_PADDING_SCALE;
        paddingT.scale.y = TUTORIAL_DARKEN_PADDING_SCALE;
        paddingT.y = -TUTORIAL_DARKEN_PADDING_SIZE;
        paddingB.scale.x = TUTORIAL_DARKEN_PADDING_SCALE;
        paddingB.scale.y = TUTORIAL_DARKEN_PADDING_SCALE;
        paddingB.y = TUTORIAL_DARKEN_PADDING_SIZE;
        this._darken.addChild(paddingT);
        this._darken.addChild(paddingB);

        this._darkenFrame.visible = false;
        this._frmMessage1.visible = false;
        this._frmMessage2.visible = false;
    }

    /**
     * Set up the current tutorial message
     * Returns whether the tutorial was opened or not
     * @public
     * @param {String} tutorialId
     * @param {Number} stepIndex
     * @param {Function} [callback]
     * @returns {Boolean}
     */
    openTutorial(tutorialId, stepIndex, callback = null) {
        // Tutorial already completed?
        if (Common.save.hasPlayedTutorial(tutorialId)) {
            if (callback) callback();
            return false;
        }

        // Specific step of current tutorial has already been opened?
        const stepsOpened = this._stepsOpened.get(tutorialId);
        if (stepsOpened && stepsOpened.includes(stepIndex)) {
            if (callback) callback();
            return false;
        }

        // Another tutorial is already opened?
        if (this._currentTutorial !== tutorialId && this.isOpened && this._currentTutorialTurns < TUTORIAL_NB_TURNS_TO_CHANGE) {
            if (callback) callback();
            return false;
        }

        this._callback = callback;

        console.log("[TutorialPopup.openTutorial] Tutorial : " + tutorialId + ", Step index : " + stepIndex);

        Game.fire(GameEvents.eTUTORIAL_HIGHLIGHT_CLEAR);

        if (!this.isOpened) {
            this.open();
        }

        // Tutorial change?
        if (tutorialId !== this._currentTutorial) {
            this._currentTutorialTurns = 0;
        }

        this._currentTutorial = tutorialId;

        this._currentStep = stepIndex;

        if (stepsOpened) {
            stepsOpened.push(stepIndex);
        } else {
            this._stepsOpened.set(this._currentTutorial, [stepIndex]);
        }

        this._tutorialToSave.push(this._currentTutorial);

        this._tutorialData = Common.tutorials.getTutorialStepData(this._currentTutorial);

        const stepData = this._tutorialData[this._currentStep];

        const pauseGame = stepData.PauseGame === true;
        if (pauseGame) {
            Game.pause("Tutorial");
        }

        if (stepData.HighlightEvent) {
            Game.fire(stepData.HighlightEvent);
        }

        this._showTutorialGraphics();

        return true;
    }

    /**
     * Reset all the tutorial steps after a save game reset
     * @private
     */
    _onResetSteps() {
        if (this._stepsOpened) {
            this._stepsOpened.clear();
        } else {
            this._stepsOpened = new Map();
        }
    }

    /**
     * A new turns begins
     * @public
     */
    beginTurn() {
        // Number of turns the current tutorial has been opened
        ++this._currentTutorialTurns;
    }

    /**
     * Proceed to the next step of the current tutorial
     * @public
     * @param {String} tutorialId
     */
    nextStep(tutorialId) {
        if (this._currentTutorial !== tutorialId) {
            return;
        }

        Game.fire(GameEvents.eTUTORIAL_HIGHLIGHT_CLEAR);

        ++this._currentStep;

        const stepsOpened = this._stepsOpened.get(tutorialId);
        if (stepsOpened) {
            stepsOpened.push(this._currentStep);
        } else {
            this._stepsOpened.set(this._currentTutorial, [this._currentStep]);
        }

        console.log("[TutorialPopup.nextStep] Tutorial : " + tutorialId + ", Step index : " + this._currentStep);

        if (this._currentStep >= this._tutorialData.length) {
            if (this._callback) {
                this._callback();
                this._callback = null;
            }

            this.close();
            Game.resume("Tutorial");
        } else {
            const stepData = this._tutorialData[this._currentStep];

            const pauseGame = stepData.PauseGame === true;
            if (pauseGame) {
                Game.pause("Tutorial");
            }

            if (stepData.HighlightEvent) {
                Game.fire(stepData.HighlightEvent);
            }

            this._showTutorialGraphics();
        }
    }

    /**
     * @protected
     * @param {Object} options
     */
    _onWindowOpening(options) {}

    /**
     * @protected
     */
    _onWindowOpened() {}

    /**
     * @protected
     */
    _onWindowClosing(options) {
        Game.fire(GameEvents.eTUTORIAL_HIGHLIGHT_CLEAR);
    }

    /**
     * @protected
     */
    _onWindowClosed(options) {
        // Tutorial suddenly closed returning to level selection or main menu
        if (options.closeSource === CloseEventSource.eCONTEXT_CHANGE) {
            Game.resume("Tutorial");

            this._currentTutorial = "";
            this._tutorialToSave = [];
            this._stepsOpened = new Map();
        }
    }

    /**
     * @public
     */
    saveProgress() {
        for (let i = 0; i < this._tutorialToSave.length; ++i) {
            Game.fire(GameEvents.eTUTORIAL_STEP_PLAYED, this._tutorialToSave[i]);
        }
    }

    /**
     * @private
     * @param {String} keyCode
     */
    _onKeyDown(keyCode) {
        // Advance in tutorial steps
        if (this._keysToAdvance.includes(keyCode)) {
            this._onInputDown();
        }
    }

    /**
     * On touch or mouse input down
     * @private
     */
    _onInputDown() {
        if (!this.isOpened) {
            return;
        }

        const data = this._tutorialData[this._currentStep];

        if ((data && data.OnTap === "NextStep") || data.OnTap === "Close") {
            if (data.PauseGame === true) {
                Game.resume("Tutorial");
            }

            switch (data.OnTap) {
                case "NextStep":
                    this.nextStep(this._currentTutorial);
                    break;

                case "Close":
                    this.close();

                    if (this._callback) {
                        this._callback();
                        this._callback = null;
                    }
                    break;
            }
        }
    }

    /**
     * Show or hide the graphics/darken of the current tutorial step
     * @private
     */
    _showTutorialGraphics() {
        const data = this._tutorialData[this._currentStep];

        const messageWidth1 = Game.utils.assertNumber(data.MessageWidth1, DEFAULT_TUTORIAL_MESSAGE_WIDTH);
        const messageHeight1 = Game.utils.assertNumber(data.MessageHeight1, DEFAULT_TUTORIAL_MESSAGE_HEIGHT);
        this._background1.width = messageWidth1;
        this._background1.height = messageHeight1;

        const messageWidth2 = Game.utils.assertNumber(data.MessageWidth2, DEFAULT_TUTORIAL_MESSAGE_WIDTH);
        const messageHeight2 = Game.utils.assertNumber(data.MessageHeight2, DEFAULT_TUTORIAL_MESSAGE_HEIGHT);
        this._background2.width = messageWidth2;
        this._background2.height = messageHeight2;

        let message1 = Game.utils.assertValue(data.MessageId1, "");
        let message2 = Game.utils.assertValue(data.MessageId2, "");

        // Adapt message for specific input method
        let replaceWith = "";
        switch (Game.input.lastInputMethod) {
            case InputMethod.eTOUCH:
            case InputMethod.eMOUSE:
                replaceWith = "POINT";
                break;
            case InputMethod.eKEYBOARD:
                replaceWith = "KEYBOARD";
                break;
            case InputMethod.eHANDSET:
                replaceWith = "HANDSET";
                break;
        }
        message1 = message1.replace("[INPUTMETHOD]", replaceWith);
        message2 = message2.replace("[INPUTMETHOD]", replaceWith);

        this._frmMessage1.visible = message1.length > 0;
        this._frmMessage2.visible = message2.length > 0;

        if (this._lblMessage1) {
            this._lblMessage1.destroy();
            this._lblMessage1 = null;
        }
        if (this._lblMessage2) {
            this._lblMessage2.destroy();
            this._lblMessage2 = null;
        }

        if (message1.length > 0) {
            this._lblMessage1 = this._frmMessage1.createText({
                localized: true,
                text: message1,
                textStyle: TUTORIAL_LABEL_STYLE,
                fontSize: TUTORIAL_LABEL_FONTSIZE,
                wrapLines: true,
                width: messageWidth1 * TUTORIAL_LABEL_WIDTH_RATIO,
                height: messageHeight1,
                inlineSpriteThreshold: TUTORIAL_INLINE_SPRITE_THRESHOLD
            });
            this._frmMessage1.x = Game.utils.assertNumber(data.MessageX1, 0);
            this._frmMessage1.y = Game.utils.assertNumber(data.MessageY1, 0);
        }

        if (message2.length > 0) {
            this._lblMessage2 = this._frmMessage2.createText({
                localized: true,
                text: message2,
                textStyle: TUTORIAL_LABEL_STYLE,
                fontSize: TUTORIAL_LABEL_FONTSIZE,
                wrapLines: true,
                width: messageWidth2 * TUTORIAL_LABEL_WIDTH_RATIO,
                height: messageHeight2,
                inlineSpriteThreshold: TUTORIAL_INLINE_SPRITE_THRESHOLD
            });
            this._frmMessage2.x = Game.utils.assertNumber(data.MessageX2, 0);
            this._frmMessage2.y = Game.utils.assertNumber(data.MessageY2, 0);
        }

        Game.audio.playSfx("IDSND_POPUP_OPEN", { ignoreNonSystemPauseEvents: true });

        this._frmMessage1.scale = TINY_INITIAL_SCALE_FACTOR;
        this._tweenMessage1 = this.createTween(this._frmMessage1)
            .to({ scale: 1 }, MODAL_OPEN_CLOSE_TIME * 1000)
            .easing(MODAL_OPEN_CURVE)
            .onComplete(() => {
                if (this._tweenMessage1) {
                    this._tweenMessage1.destroy();
                    this._tweenMessage1 = null;
                }
            })
            .start();

        this._frmMessage2.scale = TINY_INITIAL_SCALE_FACTOR;
        this._tweenMessage2 = this.createTween(this._frmMessage2)
            .to({ scale: 1 }, MODAL_OPEN_CLOSE_TIME * 1000)
            .easing(MODAL_OPEN_CURVE)
            .onComplete(() => {
                if (this._tweenMessage2) {
                    this._tweenMessage2.destroy();
                    this._tweenMessage2 = null;
                }
            })
            .start();

        this._inputZone.visible = data.OnTap === "NextStep" || data.OnTap === "Close";

        // Darken
        if (this._darkenMask) {
            this._darkenMask.destroy();
            this._darkenMask = null;
        }
        let darkenAsset = null;
        if (TUTORIAL_MASKS[this._currentTutorial]) {
            darkenAsset = TUTORIAL_MASKS[this._currentTutorial][this._currentStep];
        }
        if (darkenAsset) {
            this._darkenMask = new GameSprite(darkenAsset);
            this._darken.addChild(this._darkenMask);
        }

        this._darkenFrame.visible = data.Darken === true;
    }

    /**
     * @private
     */
    _onHighlightChangeTeam() {
        const data = this._tutorialData[this._currentStep];

        if (this._heroSelectionHighlight) {
            this._heroSelectionHighlight.visible = true;
        }

        // Hand over hero selection button
        this._animatedHand = new GameAnimatedSprite(null, Game.design.getGraphicData("TutorialHand"));
        this._animatedHand.playAnimation("finger", true, { ignoreNonSystemPauseEvents: true });
        this._frmHand.uiRoot.addChild(this._animatedHand);

        this._frmHand.x = data.HandX;
        this._frmHand.y = data.HandY;
        this._frmHand.visible = true;

        this._animatedHand.angle = Game.utils.assertNumber(data.HandRotation, 0);
        this._animatedHand.scale.x = Game.utils.assertNumber(data.HandScale, 1);
        this._animatedHand.scale.y = Game.utils.assertNumber(data.HandScale, 1);
    }

    /**
     * @private
     */
    _onHighlightCannon() {
        if (Common.tutorials.usingPointerControls) {
            const data = this._tutorialData[this._currentStep];

            // Hand over cannon
            this._animatedHand = new GameAnimatedSprite(null, Game.design.getGraphicData("TutorialHand"));
            this._animatedHand.playAnimation("finger", true, { ignoreNonSystemPauseEvents: true });
            this._frmHand.uiRoot.addChild(this._animatedHand);

            this._frmHand.x = data.HandX;
            this._frmHand.y = data.HandY;
            this._frmHand.visible = true;
        }
    }

    /**
     * @private
     */
    _onHighlightHeroAbilities() {
        if (Common.tutorials.usingKeyboardControls) {
            if (this._heroKeysHighlight1) {
                this._heroKeysHighlight1.visible = true;
            }
        }
    }

    /**
     * @private
     */
    _onHighlightHeroActivation() {
        const data = this._tutorialData[this._currentStep];

        if (Common.tutorials.usingKeyboardControls) {
            if (this._heroKeysHighlight2) {
                this._heroKeysHighlight2.visible = true;
            }
        }

        if (Common.tutorials.usingPointerControls) {
            // Hand over fully charged hero
            this._animatedHand = new GameAnimatedSprite(null, Game.design.getGraphicData("TutorialHand"));
            this._animatedHand.playAnimation("finger", true, { ignoreNonSystemPauseEvents: true });
            this._frmHand.uiRoot.addChild(this._animatedHand);

            const handPos = Common.hudHeroes.getAbilityHandPos();
            this._frmHand.x = handPos.x;
            this._frmHand.y = handPos.y;
            this._frmHand.visible = true;
        }
    }

    /**
     * @private
     */
    _onHighlightClear() {
        if (this._heroSelectionHighlight) {
            this._heroSelectionHighlight.visible = false;
        }

        if (this._heroKeysHighlight1) {
            this._heroKeysHighlight1.visible = false;
        }
        if (this._heroKeysHighlight2) {
            this._heroKeysHighlight2.visible = false;
        }

        if (this._frmHand) {
            this._frmHand.visible = false;
        }

        if (this._animatedHand) {
            this._animatedHand.stop();
            this._animatedHand.destroy();
            this._animatedHand = null;
        }
    }

    /**
     * @public
     */
    closeLevel() {
        this._currentTutorial = "";
        this._tutorialToSave = [];
        this._stepsOpened = new Map();

        if (this._tweenMessage1) {
            this._tweenMessage1.destroy();
            this._tweenMessage1 = null;
        }
        if (this._tweenMessage2) {
            this._tweenMessage2.destroy();
            this._tweenMessage2 = null;
        }

        this._onHighlightClear();
    }
}
